import { showConfirmationPrompt, AsyncButton } from '@bpinternal/ui-kit-next'
import { trackEvent } from '~/providers'
import { formatters } from '~/utils'
import { usePlanProducts } from '../hooks/usePlanProducts'
import { useAddons } from '../hooks/useAddons'
import { useSuspenseQuery } from '~/services'
import { usePurchaseAddons } from '../hooks/usePurchaseAddons'
import { useState } from 'react'
import { AddonItems } from './AddonItems'
import { AddonChangeDialog } from './AddOnChangeDialog'

type AddonsSectionProps = {
  workspaceId: string
}

export function AddonsSection({ workspaceId }: AddonsSectionProps) {
  const {
    data: { billingReadonly },
  } = useSuspenseQuery('workspaces_/$workspaceId_/billing/info', { workspaceId })

  const { currentPlan } = usePlanProducts(workspaceId)
  const { addons } = useAddons(workspaceId)

  const [addonDeltas, setAddonDeltas] = useState({
    invocation_calls: 0,
    table_row_count: 0,
    bot_count: 0,
    always_alive: 0,
    workspace_member_count: 0,
    knowledgebase_vector_storage: 0,
    storage_count: 0,
  })

  const { mutate: purchaseAddons, isPending: purchasePending } = usePurchaseAddons(workspaceId, () => {
    setAddonDeltas({
      invocation_calls: 0,
      table_row_count: 0,
      bot_count: 0,
      always_alive: 0,
      workspace_member_count: 0,
      knowledgebase_vector_storage: 0,
      storage_count: 0,
    })
  })

  const hasPendingAddonChanges = Object.values(addonDeltas).some((delta) => delta !== 0)

  const nextPaymentPrice = addons.reduce((acc, addon) => acc + addon.priceCents * addon.nextPaymentQuantity, 0)
  const nextPaymentDraftPrice = addons.reduce(
    (acc, addon) => acc + addon.priceCents * (addon.nextPaymentQuantity + addonDeltas[addon.quotaName]),
    0
  )

  const onPurchaseAddonsClick = () => {
    const purchaseAddonsEvent = {
      type: 'update_addons',
      state: 'clicked',
      upgrade: nextPaymentPrice < nextPaymentDraftPrice,
      workspaceId,
      plan: currentPlan.legacyName,
    } as const
    trackEvent(purchaseAddonsEvent)

    showConfirmationPrompt(<AddonChangeDialog addons={addons} addonDeltas={addonDeltas} />, {
      title: 'Confirm Subscription Changes',
      confirmLabel: 'Update Subscription',
      cancelLabel: 'Cancel',
      size: 'max-w-lg',
      hideCloseButton: true,
    }).then(async () => {
      trackEvent({
        ...purchaseAddonsEvent,
        state: 'confirmed',
      })

      purchaseAddons({
        addonDeltas,
        addons,
      })
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">Add-ons</h2>
      <div className="flex flex-col gap-4 sm:flex-col-reverse sm:gap-3">
        <div className="flex flex-col items-center justify-between rounded-lg bg-gray-2 p-5 max-sm:sticky max-sm:top-16 max-sm:z-[1] max-sm:shadow-md sm:flex-row sm:gap-0 sm:p-4">
          <div className="mb-4 flex w-full min-w-0 items-center justify-between sm:mb-0">
            <p className="self-start truncate text-sm font-medium">Total cost of Add-ons per month</p>
            <div className="flex">
              {nextPaymentPrice !== nextPaymentDraftPrice && (
                <div className="h-6 self-end px-1 text-muted-foreground line-through opacity-60 sm:hidden">
                  {formatters.$centdollars(nextPaymentPrice)}
                </div>
              )}
              <div className="font-semibold sm:hidden">{formatters.$centdollars(nextPaymentDraftPrice)}</div>
            </div>
          </div>
          <div className="flex w-full items-center gap-4 sm:justify-end">
            <AsyncButton
              className="w-full bg-primary sm:w-fit"
              disabled={billingReadonly || !hasPendingAddonChanges || purchasePending}
              onClick={onPurchaseAddonsClick}
              loading={purchasePending}
            >
              Update Add-ons
            </AsyncButton>
            <div className="hidden gap-1 sm:flex">
              {nextPaymentPrice !== nextPaymentDraftPrice && (
                <p className="flex w-16 justify-center text-muted-foreground line-through opacity-60">
                  {formatters.$centdollars(nextPaymentPrice)}
                </p>
              )}
              <div className="w-16 justify-center font-semibold sm:flex">
                {formatters.$centdollars(nextPaymentDraftPrice)}
              </div>
            </div>
          </div>
        </div>
        <AddonItems
          addons={addons}
          addonDeltas={addonDeltas}
          updateAddonDelta={(addonQuotaName, delta) =>
            setAddonDeltas((prev) => ({
              ...prev,
              [addonQuotaName]: prev[addonQuotaName] + delta,
            }))
          }
        />
      </div>
    </div>
  )
}
